import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { LoggerService } from './logger.service';
import { SpotlightAgentBaseService } from './spotlight-agent-base.service';

export interface ChatMessagePart {
  type: string;
  text?: string;
  toolName?: string;
  toolCallId?: string;
  state?: string;
  output?: unknown;
}

export interface ChatSession {
  id: string;
  userId: string;
  projectId: string;
  collectionId: string;
  createdAt: string;
}

export interface ChatMessage {
  role: 'user' | 'assistant' | 'tool';
  content: string;
  parts?: ChatMessagePart[];
}

export interface ChatResponse {
  text: string;
  toolCalls?: Array<{
    toolName: string;
    toolCallId?: string;
    input: Record<string, unknown>;
    output?: unknown;
  }>;
}

export interface StreamingToolCall {
  toolName: string;
  toolCallId?: string;
  state: 'pending' | 'output-available';
  output?: unknown;
}

export interface StreamingCallbacks {
  onTextChunk: (text: string) => void;
  onToolCall: (tool: StreamingToolCall) => void;
  onComplete: () => void;
  onError: (error: Error) => void;
}

export interface ResearchIdea {
  emojiIcon: string;
  title: string;
  description: string;
}

export interface AnalysisResult {
  summary: string;
  researchIdeas: ResearchIdea[];
}

interface StreamToolPart {
  toolName?: string;
  output?: unknown;
  result?: unknown;
  input?: unknown;
  toolCallId?: string;
}

interface StreamPart {
  type?: string;
  delta?: string;
  text?: string;
  toolName?: string;
  output?: unknown;
  result?: unknown;
  input?: unknown;
  inputTextDelta?: string;
  state?: string;
  toolCallId?: string;
  tool?: StreamToolPart;
  toolCall?: StreamToolPart;
  toolResult?: StreamToolPart;
  toolOutput?: StreamToolPart;
}

interface PendingToolInput {
  toolName: string;
  input?: unknown;
  inputText?: string;
}

export interface ChatSessionWithName extends ChatSession {
  name?: string;
  messages?: ChatMessage[];
}

/**
 * Service for communicating with the Spotlight Agent chat API.
 * Handles session management and streaming chat requests.
 */
@Injectable({ providedIn: 'root' })
export class SpotlightAgentChatService extends SpotlightAgentBaseService {
  private readonly http = inject(HttpClient);
  private readonly logger = inject(LoggerService);

  private currentSession: ChatSession | null = null;
  private readonly pendingToolInputs = new Map<string, PendingToolInput>();
  private readonly toolNamesByCallId = new Map<string, string>();

  /**
   * Get or create a chat session for the given project/collection.
   */
  async getOrCreateSession(projectId: string, collectionId: string): Promise<ChatSession> {
    const { session, baseUrl, env } = await this.getAgentRequestContext();
    const userId = session.info2?.user_id;

    if (
      this.currentSession &&
      this.currentSession.projectId === projectId &&
      this.currentSession.collectionId === collectionId
    ) {
      return this.currentSession;
    }

    // Create new session
    const createUrl = `${baseUrl}/api/chat/sessions`;
    const newSession = await firstValueFrom(
      this.http.post<ChatSession>(
        createUrl,
        { userId, projectId, collectionId, env },
        { headers: { Authorization: session.id } }
      )
    );
    return newSession;
  }

  /**
   * Send a chat message and get a response.
   * Uses the agent-chat endpoint for agentic features (search, analysis).
   */
  async sendMessage(
    sessionId: string,
    messages: ChatMessage[],
    projectId: string,
    collectionId: string,
    persist: boolean = true
  ): Promise<ChatResponse> {
    const { session, baseUrl, env } = await this.getAgentRequestContext();

    const url = `${baseUrl}/api/chat`;
    const body = {
      sessionId,
      messages: messages.map((m) => ({
        role: m.role,
        parts: [{ type: 'text', text: m.content }],
      })),
      token: session.id,
      summary: '',
      env,
      persist,
    };

    try {
      const response = await firstValueFrom(
        this.http.post<any>(url, body, {
          headers: {
            Authorization: session.id,
            'Content-Type': 'application/json',
          },
        })
      );

      const toolCalls = this.normalizeToolCalls(response);
      // The agent-chat endpoint returns streaming data, but for now we handle simple response
      return {
        text: response.text || response.content || 'No response received',
        toolCalls,
      };
    } catch (error: any) {
      this.logger.error('Chat API error:', { error });
      throw new Error(error?.error?.error || 'Failed to send message');
    }
  }

  private normalizeToolCalls(response: any): ChatResponse['toolCalls'] {
    const toolCalls: ChatResponse['toolCalls'] = Array.isArray(response.toolCalls)
      ? response.toolCalls
      : [];
    const toolResults: Array<{
      toolName?: string;
      result?: unknown;
      output?: unknown;
      toolCallId?: string;
    }> = Array.isArray(response.toolResults) ? response.toolResults : [];
    const dynamicToolResults: Array<{
      toolName?: string;
      result?: unknown;
      output?: unknown;
      toolCallId?: string;
    }> = Array.isArray(response.dynamicToolResults) ? response.dynamicToolResults : [];

    if (toolResults.length === 0 && dynamicToolResults.length === 0) {
      return toolCalls;
    }

    const mergedResults = [...toolResults, ...dynamicToolResults];
    const resultsById = new Map<
      string,
      { toolName?: string; result?: unknown; output?: unknown }
    >();
    const resultsByToolName = new Map<
      string,
      Array<{ toolName?: string; result?: unknown; output?: unknown }>
    >();

    for (const result of mergedResults) {
      if (result.toolCallId) {
        resultsById.set(result.toolCallId, result);
      } else if (result.toolName) {
        const queue = resultsByToolName.get(result.toolName) ?? [];
        queue.push(result);
        resultsByToolName.set(result.toolName, queue);
      }
    }

    const updatedToolCalls = toolCalls.map((toolCall) => {
      if (toolCall.output) {
        return toolCall;
      }
      const toolCallIdRaw = toolCall.toolCallId ?? toolCall.input?.toolCallId;
      const toolCallId = typeof toolCallIdRaw === 'string' ? toolCallIdRaw : undefined;
      const matchById = toolCallId ? resultsById.get(toolCallId) : undefined;
      if (matchById && toolCallId) {
        resultsById.delete(toolCallId);
      }
      const matchByName =
        !matchById && toolCall.toolName
          ? resultsByToolName.get(toolCall.toolName)?.shift()
          : undefined;
      const match = matchById ?? matchByName;
      if (match) {
        return {
          ...toolCall,
          output: match.output ?? match.result,
        };
      }
      return toolCall;
    });

    const remainingResults: Array<{
      toolName?: string;
      result?: unknown;
      output?: unknown;
      toolCallId?: string;
    }> = [
      ...Array.from(resultsById.entries()).map(([toolCallId, result]) => ({
        ...result,
        toolCallId,
      })),
      ...Array.from(resultsByToolName.values()).flat(),
    ];

    return [
      ...updatedToolCalls,
      ...remainingResults
        .filter((result) => !!result.toolName)
        .map((result) => ({
          toolName: result.toolName as string,
          toolCallId: result.toolCallId,
          input: {},
          output: result.output ?? result.result,
        })),
    ];
  }

  /**
   * Send a streaming chat message using fetch + ReadableStream.
   * Parses the AI SDK data stream format and invokes callbacks progressively.
   *
   * @param signal Optional AbortSignal to cancel the in-flight request.
   */
  async sendStreamingMessage(
    sessionId: string,
    messages: ChatMessage[],
    callbacks: StreamingCallbacks,
    signal?: AbortSignal
  ): Promise<void> {
    this.pendingToolInputs.clear();
    this.toolNamesByCallId.clear();

    const { session, baseUrl, env } = await this.getAgentRequestContext();

    const url = `${baseUrl}/api/chat`;
    const body = {
      sessionId,
      messages: messages.map((m) => ({
        role: m.role,
        parts: [{ type: 'text', text: m.content }],
      })),
      token: session.id,
      summary: '',
      env,
    };

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          Authorization: session.id,
          'Content-Type': 'application/json',
          Accept: 'text/event-stream',
        },
        body: JSON.stringify(body),
        signal,
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('SESSION_EXPIRED');
        }
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      const contentType = response.headers.get('content-type') ?? '';
      if (contentType.includes('application/json')) {
        const data = await response.json().catch(() => ({}));
        const text = data.text || data.content;
        if (typeof text === 'string' && text.length > 0) {
          callbacks.onTextChunk(text);
          callbacks.onComplete();
          return;
        }
        throw new Error('Empty response from chat API');
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('Response body is not readable');
      }

      const decoder = new TextDecoder();
      let buffer = '';
      let hasChunk = false;

      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          break;
        }

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.trim()) {
            continue;
          }
          hasChunk = true;
          this.parseStreamLine(line, callbacks);
        }
      }

      // Process remaining buffer
      if (buffer.trim()) {
        hasChunk = true;
        this.parseStreamLine(buffer, callbacks);
      }

      if (!hasChunk) {
        throw new Error('Empty stream from chat API');
      }

      callbacks.onComplete();
    } catch (error: any) {
      this.logger.error('Streaming chat error:', { error });
      callbacks.onError(error);
    }
  }

  /**
   * Parse a single line from the AI SDK data stream.
   * Format: <type>:<json-data>
   */
  private parseStreamLine(line: string, callbacks: StreamingCallbacks): void {
    const trimmed = line.trim();
    if (!trimmed) {
      return;
    }

    if (trimmed.startsWith('data:')) {
      line = trimmed.slice('data:'.length).trim();
    } else if (trimmed.startsWith('event:')) {
      return;
    }

    if (line === '[DONE]') {
      return;
    }

    if (line.startsWith('{')) {
      this.parseJsonStreamPart(line, callbacks);
      return;
    }

    // Legacy AI SDK stream format: type:json
    const colonIndex = line.indexOf(':');
    if (colonIndex === -1) {
      return;
    }

    const type = line.substring(0, colonIndex);
    const jsonStr = line.substring(colonIndex + 1);

    try {
      switch (type) {
        case '0': // text delta
          const text = JSON.parse(jsonStr);
          if (typeof text === 'string') {
            callbacks.onTextChunk(text);
          }
          break;
        case 'b': // tool call start
          const toolStart = JSON.parse(jsonStr);
          if (toolStart?.toolName) {
            callbacks.onToolCall({
              toolName: toolStart.toolName,
              toolCallId:
                typeof toolStart.toolCallId === 'string' ? toolStart.toolCallId : undefined,
              state: 'pending',
            });
          }
          break;
        case 'c': // tool call result
          const toolResult = JSON.parse(jsonStr);
          if (toolResult?.toolName) {
            callbacks.onToolCall({
              toolName: toolResult.toolName,
              toolCallId:
                typeof toolResult.toolCallId === 'string' ? toolResult.toolCallId : undefined,
              state: 'output-available',
              output: toolResult.output ?? toolResult.result,
            });
          }
          break;
        case 'd': // finish reason
        case 'e': // error
          // These are handled by onComplete/onError
          break;
        default:
          // Unknown type, ignore
          break;
      }
    } catch (e) {
      this.logger.warn('[SpotlightAgentChat] Failed to parse legacy stream line:', {
        line: line.substring(0, 200),
        error: e,
      });
    }
  }

  private parseJsonStreamPart(raw: string, callbacks: StreamingCallbacks): void {
    try {
      const parsed = JSON.parse(raw) as StreamPart;
      if (!parsed?.type) {
        return;
      }

      let resolvedToolName = this.resolveStreamValue<string>([
        parsed.toolName,
        parsed.tool?.toolName,
        parsed.toolCall?.toolName,
        parsed.toolResult?.toolName,
        parsed.toolOutput?.toolName,
      ]);
      const resolvedOutput = this.resolveStreamValue<unknown>([
        parsed.output,
        parsed.result,
        parsed.toolCall?.output,
        parsed.toolCall?.result,
        parsed.toolResult?.output,
        parsed.toolResult?.result,
        parsed.toolOutput?.output,
        parsed.toolOutput?.result,
      ]);
      const toolCallId = this.resolveToolCallId(parsed);
      if (toolCallId && resolvedToolName) {
        this.toolNamesByCallId.set(toolCallId, resolvedToolName);
      }
      if (toolCallId && !resolvedToolName) {
        const mappedToolName = this.toolNamesByCallId.get(toolCallId);
        if (mappedToolName) {
          resolvedToolName = mappedToolName;
        }
      }

      switch (parsed.type) {
        case 'text':
          if (typeof parsed.text === 'string') {
            callbacks.onTextChunk(parsed.text);
          }
          break;
        case 'text-delta':
          if (typeof parsed.delta === 'string') {
            callbacks.onTextChunk(parsed.delta);
          } else if (typeof parsed.text === 'string') {
            callbacks.onTextChunk(parsed.text);
          }
          break;
        case 'tool-call':
          if (resolvedToolName) {
            callbacks.onToolCall({
              toolName: resolvedToolName,
              toolCallId,
              state: 'pending',
            });
          }
          break;
        case 'tool-input':
        case 'tool-input-delta':
          this.trackToolInput(parsed, resolvedToolName, toolCallId, callbacks);
          break;
        case 'tool-result':
        case 'tool-output':
          if (resolvedToolName) {
            callbacks.onToolCall({
              toolName: resolvedToolName,
              toolCallId,
              state: 'output-available',
              output: resolvedOutput,
            });
          }
          if (toolCallId) {
            this.pendingToolInputs.delete(toolCallId);
            this.toolNamesByCallId.delete(toolCallId);
          }
          break;
        case 'dynamic-tool':
          if (resolvedToolName) {
            const state = parsed.state === 'output-available' ? 'output-available' : 'pending';
            callbacks.onToolCall({
              toolName: resolvedToolName,
              toolCallId,
              state,
              output: resolvedOutput,
            });
          }
          if (toolCallId && parsed.state === 'output-available') {
            this.pendingToolInputs.delete(toolCallId);
            this.toolNamesByCallId.delete(toolCallId);
          }
          break;
        default:
          break;
      }
    } catch (error) {
      this.logger.warn('[SpotlightAgentChat] Failed to parse JSON stream part:', {
        raw: raw.substring(0, 200),
        error,
      });
    }
  }

  private resolveToolCallId(parsed: StreamPart): string | null {
    return (
      parsed.toolCallId ||
      parsed.tool?.toolCallId ||
      parsed.toolCall?.toolCallId ||
      parsed.toolResult?.toolCallId ||
      parsed.toolOutput?.toolCallId ||
      null
    );
  }

  private resolveStreamValue<T>(values: Array<T | null | undefined>): T | undefined {
    return values.find((value): value is T => value != null);
  }

  private trackToolInput(
    parsed: StreamPart,
    toolName: string | undefined,
    toolCallId: string | null,
    callbacks: StreamingCallbacks
  ): void {
    if (!toolName || !toolCallId) {
      return;
    }
    const existing = this.pendingToolInputs.get(toolCallId);
    const nextInputText = parsed.inputTextDelta
      ? `${existing?.inputText ?? ''}${parsed.inputTextDelta}`
      : existing?.inputText;
    const nextInput = parsed.input ?? existing?.input;
    this.pendingToolInputs.set(toolCallId, {
      toolName,
      input: nextInput,
      inputText: nextInputText,
    });
    callbacks.onToolCall({
      toolName,
      toolCallId,
      state: 'pending',
    });
  }

  /**
   * Fetch persisted tool results for a session from the backend.
   * Used to recover chart outputs without re-invoking the agent.
   */
  async getToolResults(
    sessionId: string
  ): Promise<Array<{ toolName: string; toolCallId?: string; output: unknown }>> {
    const { session, baseUrl, env } = await this.getAgentRequestContext();
    const url = `${baseUrl}/api/chat/sessions/${sessionId}/tool-results`;
    try {
      const results = await firstValueFrom(
        this.http.get<Array<{ toolName: string; toolCallId?: string; output: unknown }>>(url, {
          headers: { Authorization: session.id, 'x-spotlight-env': env },
        })
      );
      return results ?? [];
    } catch (error) {
      this.logger.warn('[SpotlightAgentChat] Failed to fetch tool results:', { error });
      return [];
    }
  }

  getCurrentSessionId(): string | null {
    return this.currentSession?.id ?? null;
  }

  setCurrentSession(session: ChatSession | null): void {
    this.currentSession = session;
  }

  clearSession(): void {
    this.currentSession = null;
  }

  /**
   * Analyse collection to get research ideas (prompt suggestions).
   */
  async analyseCollection(
    projectId: string,
    collectionId: string,
    contentChannel?: string,
    hasSections?: boolean
  ): Promise<AnalysisResult> {
    const { session, baseUrl, env } = await this.getAgentRequestContext();

    const url = `${baseUrl}/api/analyse`;
    const body = {
      projectId,
      collectionId,
      token: session.id,
      env,
      contentChannel,
      hasSections,
    };

    const response = await firstValueFrom(
      this.http.post<AnalysisResult>(url, body, {
        headers: {
          Authorization: session.id,
          'Content-Type': 'application/json',
        },
      })
    );
    return response;
  }

  /**
   * Get all chat sessions for a project, optionally filtered by collection.
   */
  async getChatSessions(projectId: string, collectionId?: string): Promise<ChatSessionWithName[]> {
    const { session, baseUrl, env } = await this.getAgentRequestContext();
    const userId = session.info2?.user_id;

    const params = new URLSearchParams({ userId, projectId });
    if (collectionId) {
      params.set('collectionId', collectionId);
    }
    const url = `${baseUrl}/api/chat/sessions?${params.toString()}`;
    try {
      const sessions = await firstValueFrom(
        this.http.get<ChatSessionWithName[]>(url, {
          headers: { Authorization: session.id, 'x-spotlight-env': env },
        })
      );
      return sessions || [];
    } catch (error) {
      return [];
    }
  }

  /**
   * Load a specific chat session with messages.
   */
  async loadChatSession(sessionId: string): Promise<ChatSessionWithName | null> {
    const { session, baseUrl, env } = await this.getAgentRequestContext();

    const url = `${baseUrl}/api/chat/sessions/${sessionId}`;
    try {
      const chatSession = await firstValueFrom(
        this.http.get<ChatSessionWithName>(url, {
          headers: { Authorization: session.id, 'x-spotlight-env': env },
        })
      );
      this.currentSession = chatSession;
      return chatSession;
    } catch (error) {
      this.logger.error('Failed to load chat session:', { error });
      return null;
    }
  }

  /**
   * Delete a chat session.
   */
  async deleteChatSession(sessionId: string): Promise<boolean> {
    const { session, baseUrl, env } = await this.getAgentRequestContext();

    const url = `${baseUrl}/api/chat/sessions/${sessionId}`;
    try {
      await firstValueFrom(
        this.http.delete(url, {
          headers: { Authorization: session.id, 'x-spotlight-env': env },
        })
      );
      return true;
    } catch (error) {
      this.logger.error('Failed to delete chat session:', { error });
      return false;
    }
  }

  /**
   * Rename a chat session.
   */
  async renameChatSession(sessionId: string, name: string): Promise<boolean> {
    const { session, baseUrl, env } = await this.getAgentRequestContext();

    const url = `${baseUrl}/api/chat/sessions/${sessionId}`;
    try {
      await firstValueFrom(
        this.http.put(
          url,
          { name },
          {
            headers: {
              Authorization: session.id,
              'Content-Type': 'application/json',
              'x-spotlight-env': env,
            },
          }
        )
      );
      return true;
    } catch (error) {
      this.logger.error('Failed to rename chat session:', { error });
      return false;
    }
  }
}
